import { useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  basePatientTabsCollection,
  MedicationOrderEligibleProviderType,
  PatientTabs,
  PatientTabsDefinition,
} from '~/lib/constants/patients.constants';

export const isMedicationOrderEligibleProviderType = (
  providerType: string | null | undefined,
): boolean =>
  !!providerType &&
  Object.values(MedicationOrderEligibleProviderType).includes(
    providerType as MedicationOrderEligibleProviderType,
  );

export type PatientTabsCollectionOptions = {
  isEprescribeIntegrationEnabled?: boolean;
  isSelfMonitoringToolsEnabled?: boolean;
  currentUserProviderType?: string | null;
};

export const getPatientTabsCollection = ({
  isEprescribeIntegrationEnabled = false,
  isSelfMonitoringToolsEnabled = false,
  currentUserProviderType,
}: PatientTabsCollectionOptions = {}): Record<
  PatientTabs,
  PatientTabsDefinition
> => {
  const canViewMedicationsTab =
    isEprescribeIntegrationEnabled &&
    isMedicationOrderEligibleProviderType(currentUserProviderType);

  return Object.fromEntries(
    Object.entries(basePatientTabsCollection).filter(([key]) => {
      if (
        (!isSelfMonitoringToolsEnabled && key === PatientTabs.JOURNAL) ||
        (!canViewMedicationsTab && key === PatientTabs.MEDICATIONS)
      ) {
        return false;
      }

      return true;
    }),
  ) as Record<PatientTabs, PatientTabsDefinition>;
};

const usePatientTabsCollection = (currentUserProviderType?: string | null) => {
  const {
    enableSelfMonitoringTools: isSelfMonitoringToolsEnabled,
    eprescribeIntegration: isEprescribeIntegrationEnabled,
  } = useFlags<LDFlags>();

  return useMemo(
    () =>
      getPatientTabsCollection({
        isEprescribeIntegrationEnabled,
        isSelfMonitoringToolsEnabled,
        currentUserProviderType,
      }),
    [
      isEprescribeIntegrationEnabled,
      isSelfMonitoringToolsEnabled,
      currentUserProviderType,
    ],
  );
};

const PatientTabsCollection = getPatientTabsCollection();

export { usePatientTabsCollection, PatientTabsCollection };
